import { Anchor } from '@components/atoms/Anchor';
import GridBox from '@components/atoms/GridBox';
import Spacing from '@components/atoms/Spacing';
import {
  Heading3,
  LightParagraph,
  Paragraph,
} from '@components/atoms/Typography';
import PrimaryButton from '@components/atoms/buttons/PrimaryButton';
import BREAKPOINTS from '@lib/constants/breakpoints';
import COLORS from '@lib/constants/colors';
import ROUTES from '@lib/constants/routes';
import {
  calculateVehicleLoanMensalidade,
  medianTaxaJurosAoMes,
  vehiclModelYear,
} from '@lib/data/loan';
import { smoothScrool } from '@lib/helpers/ui';
import { FipeVehicleType } from '@prisma/client';
import { integerToCurrency } from 'helpers';
import Link from 'next/link';
import styled from 'styled-components';

const Container = styled.div`
  padding: 0;
  border: 1px solid #ccd6f5;
  border-radius: 16px;
  box-shadow: 0 0 30px rgba(84, 117, 222, 0.11);
  max-width: 458px;
  background-color: #fff;
  margin-left: auto;
  h3 {
    font-size: 18px;
  }

  @media screen and (max-width: ${BREAKPOINTS.medium}) {
    max-width: 100%;
    margin-left: 0;
  }
`;

const Section = styled.div`
  border-bottom: 1px solid #ccd6f5;
  padding: 20px;
`;

const ValueLabel = styled(Paragraph)`
  display: flex;
  justify-content: space-between;
  align-items: center;

  span {
    font-size: 14px;
    color: #7a7a7a;
  }

  strong {
    font-size: 18px;
    font-weight: normal;
  }
  margin-bottom: 8px;
`;

const HelpText = styled(Paragraph)`
  display: flex;
  align-items: flex-start;
  font-size: 12px;
  line-height: 18px;
  color: ${COLORS.gray};
`;

export default function LoanResumeCard({
  totalValue,
  entrada,
  parcelas,
  modelYear,
  type,
}: {
  totalValue: number;
  entrada: number;
  parcelas: number;
  modelYear: number;
  type: FipeVehicleType;
}) {
  const emojiByType = {
    car: '🚗',
    motorcycle: '🏍',
    trucks: '🚚',
  };

  return (
    <Container>
      <Section>
        <GridBox
          justifyContent="flex-start"
          alignItems="center"
          flexWrap="nowrap"
        >
          <div style={{ marginRight: 24 }}>
            <span style={{ fontSize: 32 }}>
              {emojiByType[type] || emojiByType.car}
            </span>
          </div>
          <div>
            <Heading3>
              Veículo no valor de {integerToCurrency(totalValue)}
            </Heading3>
            {Number(modelYear) > 0 && (
              <LightParagraph>{vehiclModelYear(modelYear)}</LightParagraph>
            )}
          </div>
        </GridBox>
      </Section>
      <Section>
        <ValueLabel>
          <span>Valor Entrada</span>
          <strong>{integerToCurrency(entrada)}</strong>
        </ValueLabel>
        <ValueLabel>
          <span>Valor Financiado</span>
          <strong>{integerToCurrency(totalValue - entrada)}</strong>
        </ValueLabel>
      </Section>
      <Section>
        <ValueLabel>
          <span>Taxa de juros média (a.m.)</span>
          <strong>{(medianTaxaJurosAoMes * 100).toFixed(2)}%</strong>
        </ValueLabel>
        <ValueLabel>
          <span>Parcelas</span>
          <strong
            style={{
              fontWeight: 'bold',
              fontSize: '22px',
            }}
          >
            {parcelas}x de{' '}
            {integerToCurrency(
              calculateVehicleLoanMensalidade(
                totalValue - entrada,
                parcelas,
                medianTaxaJurosAoMes,
              ),
            )}
          </strong>
        </ValueLabel>
        <div
          style={{
            fontSize: 13,
            textAlign: 'center',
            backgroundColor: '#f9f9f9',
            borderRadius: 8,
            padding: 12,
          }}
        >
          <Paragraph>
            Que tal simular um seguro para o seu veículo? <br />
            <Link href={ROUTES.startForm()} passHref>
              <Anchor>Simule agora nas melhores seguradoras</Anchor>
            </Link>
          </Paragraph>
        </div>
      </Section>
      <Section style={{ border: 'none' }}>
        <HelpText>
          Valores utilizados no simulador de financiamento (referentes à data de
          última atualização - esses valores podem alterar de acordo com o
          mercado)
        </HelpText>
        <Spacing top={4} />
        <a
          href="#bancos"
          onClick={(e) => {
            e.preventDefault();
            smoothScrool('bancos');
          }}
        >
          <PrimaryButton pink isBlock>
            Ver Valores Por Banco
          </PrimaryButton>
        </a>
      </Section>
    </Container>
  );
}
